import { useState } from "react";
import DeeperLookIcon_Trans from "../../../assets/UI/DeeperLookIcon_Trans.png";
import HeaderNavNTM from "../../../Components/Header/Header_Nav_NoBottomMargin/Header_Nav_NBM";
import "../DeeperLook.css"; 


export default function EmotionsDLook() {

const categories = {

    Emotions_DL_Overview: {
        title: "Emotions in Korean: Overview",
        content: [
            { type: "subheader", content: "Overview of Emotions" },
            { type: "paragraph", content: "Emotions are a universal part of human experience, and being able to express them in Korean helps you connect with others, describe how you feel, and respond naturally in conversations." },
            { type: "paragraph", content: "In Korean, emotions can be expressed as adjectives, verbs, or phrases, and the way you describe feelings can vary depending on intensity, context, and politeness. Learning these words and expressions will allow you to:" },
            { type: "list", items: [
                "Recognize and name common emotions",
                "Express positive, negative, and subtle feelings",
                "Use emotions naturally in everyday conversation"
            ] },
            { type: "paragraph", content: "💡 Tip: Korean often uses context rather than pronouns, so emotions are frequently expressed simply with the adjective or verb, and the subject is implied." }
        ]
    },
    
    BasicEmotionVocabulary: {
        title: "Basic Emotion Vocabulary",
        content: [
            { type: "subheader", content: "Overview of Basic Emotions" },
            { type: "paragraph", content: "These are the core words for the most common feelings in Korean. Learning these first helps you quickly describe your own emotions and understand others." },
            { type: "subheader", content: "Common Emotions" },
            { 
                type: "table", 
                rows: [
                    ["English", "Korean (Hangul)", "Notes"],
                    ["Happy", "행복해요 (haeng-bok-hae-yo) / 기뻐요 (gi-ppeo-yo)", "행복하다 is more formal; 기쁘다 is more casual"],
                    ["Sad", "슬퍼요 (seul-peo-yo)", "Can be used to describe situations or feelings"],
                    ["Angry", "화나요 (hwa-na-yo) / 화가 나요 (hwa-ga na-yo)", "Both mean 'I’m angry'"],
                    ["Scared", "무서워요 (mu-seo-wo-yo)", "Can describe fear of situations or objects"],
                    ["Surprised", "놀라요 (nol-la-yo)", "Often used when something unexpected happens"],
                    ["Tired", "피곤해요 (pi-gon-hae-yo)", "Common in daily conversation"],
                    ["Excited", "신나요 (sin-na-yo)", "Expressing anticipation or joy"],
                    ["Embarrassed", "부끄러워요 (bu-kkeu-reo-wo-yo)", "Feeling shy or embarrassed"]
                ]
            },
            { type: "subheader", content: "Examples" },
            { type: "paragraph", content: "저는 오늘 행복해요. → I am happy today." },
            { type: "paragraph", content: "그는 화가 나 있어요. → He is angry." },
            { type: "paragraph", content: "너 지금 피곤해 보여요. → You look tired right now." },
            { type: "paragraph", content: "💡 Tip: Most emotions can be used with -해요 for polite speech. You can also change the formality depending on the situation." }
        ]
    },
    PositiveNegativeEmotions: {
        title: "Positive and Negative Emotions",
        content: [
            { type: "subheader", content: "Overview" },
            { type: "paragraph", content: "Korean has words for a wide range of positive and negative feelings. Knowing the difference helps you express yourself clearly and understand others’ moods." },
            { type: "subheader", content: "Positive Emotions" },
            { 
                type: "table", 
                rows: [
                    ["English", "Korean", "Notes"],
                    ["Joy / Happiness", "행복해요 (haeng-bok-hae-yo)", "General happiness"],
                    ["Excited", "신나요 (sin-na-yo)", "Feeling thrilled or eager"],
                    ["Proud", "자랑스러워요 (ja-rang-seu-reo-wo-yo)", "Pride in oneself or others"],
                    ["Calm / Relaxed", "편안해요 (pyeon-an-hae-yo)", "Comfortable and relaxed"]
                ]
            },
            { type: "subheader", content: "Negative Emotions" },
            { 
                type: "table", 
                rows: [
                    ["English", "Korean", "Notes"],
                    ["Sad / Unhappy", "슬퍼요 (seul-peo-yo)", "Feeling sorrow or disappointment"],
                    ["Angry", "화나요 (hwa-na-yo)", "Frustration or anger"],
                    ["Worried", "걱정돼요 (geok-jeong-dwae-yo)", "Concerned about a situation"],
                    ["Lonely", "외로워요 (oe-ro-wo-yo)", "Feeling alone or isolated"],
                    ["Embarrassed", "부끄러워요 (bu-kkeu-reo-wo-요)", "Shyness or discomfort"]
                ]
            },
            { type: "subheader", content: "Examples" },
            { type: "paragraph", content: "저는 오늘 신나요! → I’m excited today!" },
            { type: "paragraph", content: "그는 걱정돼 보여요. → He looks worried." },
            { type: "paragraph", content: "그녀는 슬퍼하고 있어요. → She is feeling sad." },
            { type: "paragraph", content: "💡 Tip: Some emotions can change meaning slightly depending on context. For example, 신나요 can mean both 'excited' and 'having fun,' depending on the situation." }
        ]
    },

    IntensifiersModifiers: {
        title: "Intensifiers and Modifiers",
        content: [
            { type: "subheader", content: "Overview of Intensifiers and Modifiers" },
            { type: "paragraph", content: "Korean allows you to adjust the intensity of emotions using modifiers. These words help you say whether you feel a little, very, or extremely something." },
            { type: "subheader", content: "Common Modifiers" },
            { 
                type: "table", 
                rows: [
                    ["English", "Korean", "Notes"],
                    ["Very / Really", "아주 (aju), 매우 (maeu)", "Formal and casual usage"],
                    ["Extremely", "너무 (neo-mu)", "Common in spoken Korean"],
                    ["A little / Slightly", "조금 (jo-geum)", "Can soften the feeling"],
                    ["So / Super", "진짜 (jin-jja)", "Informal, casual"],
                    ["Totally / Completely", "완전히 (wan-jeon-hi)", "Strong emphasis, formal or informal"]
                ]
            },
            { type: "subheader", content: "Examples" },
            { type: "paragraph", content: "저는 오늘 너무 행복해요. → I am very happy today." },
            { type: "paragraph", content: "그는 조금 슬퍼 보여요. → He looks a little sad." },
            { type: "paragraph", content: "신나요! → Excited! → 진짜 신나요! → Really excited!" },
            { type: "paragraph", content: "그 영화 보고 완전히 화가 났어요. → I was completely angry after watching that movie." },
            { type: "subheader", content: "Try It Yourself!" },
            { type: "paragraph", content: "Place modifiers before the emotion word. For example:\n\n조금 피곤해요 → a little tired\n\n너무 화가 나요 → very angry" }
        ]
    },
    UsingEmotionsInSentences: {
        title: "Using Emotions in Sentences",
        content: [
            { type: "subheader", content: "Overview" },
            { type: "paragraph", content: "Emotion words in Korean can often be used as adjectives or verbs, allowing you to describe feelings or states naturally in sentences." },
            { type: "subheader", content: "Sentence Structures" },
            { type: "paragraph", content: "Subject + Emotion + -해요 (polite form)" },
            { type: "paragraph", content: "저는 행복해요. → I am happy." },
            { type: "paragraph", content: "그는 슬퍼요. → He is sad." },
            { type: "paragraph", content: "Subject + Emotion + -고 있어요 (ongoing action/state)" },
            { type: "paragraph", content: "나는 화가 나고 있어요. → I am getting angry." },
            { type: "paragraph", content: "그녀는 걱정하고 있어요. → She is worried." },
            { type: "subheader", content: "Adding Modifiers for Intensity" },
            { type: "paragraph", content: "오늘 너무 신나요! → I’m really excited today!" },
            { type: "paragraph", content: "조금 피곤해요. → I’m a little tired." },
            { type: "subheader", content: "Asking About Someone Else" },
            { type: "paragraph", content: "기분이 어때요? → How do you feel?" },
            { type: "paragraph", content: "오늘 행복해요? → Are you happy today?" },
            { type: "paragraph", content: "💡 Tip: Korean often omits the subject when it’s clear from context. For example:\n\n행복해요 → I’m happy / You’re happy / He’s happy (context-dependent)" }
        ]
    },

    EverydayExpressionsEmotions: {
        title: "Everyday Expressions with Emotions",
        content: [
            { type: "subheader", content: "Overview" },
            { type: "paragraph", content: "Koreans often use set phrases to express feelings naturally in conversation. Learning these will help you respond appropriately and sound more fluent." },
            { type: "subheader", content: "Common Expressions" },
            { type: "paragraph", content: "기분이 좋아요. → I feel good / I’m in a good mood." },
            { type: "paragraph", content: "기분이 안 좋아요. → I don’t feel good / I’m in a bad mood." },
            { type: "paragraph", content: "속상해요. → I feel upset." },
            { type: "paragraph", content: "긴장돼요. → I’m nervous." },
            { type: "paragraph", content: "설레요. → I’m excited / my heart is fluttering." },
            { type: "paragraph", content: "화가 나요. → I’m angry." },
            { type: "paragraph", content: "피곤해요. → I’m tired." },
            { type: "subheader", content: "Examples in Conversation" },
            { type: "paragraph", content: "A: 오늘 기분이 어때요? → How are you feeling today?" },
            { type: "paragraph", content: "B: 조금 슬퍼요. → I’m a little sad." },
            { type: "paragraph", content: "A: 시험 준비 다 했어요? → Did you finish preparing for the test?" },
            { type: "paragraph", content: "B: 네, 그런데 아직 긴장돼요. → Yes, but I’m still nervous." },
            { type: "paragraph", content: "💡 Tip: Many of these expressions can stand alone in casual conversation. Context often makes the subject clear, so you don’t always need to say 'I.'" }
        ]
    }
 }
 
 
   //Make overveiw the default catgory selected when page loads
   const [selectedCategory, setSelectedCategory] = useState("Emotions_DL_Overview");
 
 
   return (
     <div>
       
       <HeaderNavNTM GoBack_to="/EmotionsNav"/>
 
       <div className="blue-header-dlook">
         <img
           className="DeeperLookImage-dlook"
           src={DeeperLookIcon_Trans}
           alt="Deeper Look"
         />
       </div>

       <div className="panel-container-dlook">

         <div className="side-panel-dlook">
           {Object.keys(categories).map(key => (

             <p
               key={key}

               className={key === selectedCategory 
                 ? "category-active" 
                 : "category-inactive"}

               onClick={() => setSelectedCategory(key)}
             >              
               {categories[key].title}
             </p>
           ))}
         </div>
 
         {/* Text panel */}
     <div className="text-panel-dlook">
        {(categories[selectedCategory]?.content ?? []).map((item, index) => {

 
            switch (item.type) {

  
             case "subheader":
                 return <h3 key={index} className="subheader-dlook">{item.content}</h3>;
 
             case "paragraph":
                 return <p key={index}>{item.content}</p>;
 
             case "list":
                 return (
                 <ul key={index} className="list-dlook">
                     {item.items.map((bullet, i) => (
                     <li key={i}>{bullet}</li>
                     ))}
                 </ul>
                 );
 
             case "table":
                 return (
                 <table key={index} className="table-dlook">
                     <tbody>
                     {item.rows.map((row, r) => (
                         <tr key={r}>
                         {row.map((cell, c) => (
                             <td key={c}>{cell}</td>
                         ))}
                         </tr>
                     ))}
                     </tbody>
                 </table>
                 );
 
             default:
                 return null;

             }
             })}
         </div>
 
       </div>
      
       {/* End Body */}
     
     
     
     </div>
   );{/* End return */}
 } 
 
